import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Card,
  Row,
  Col,
  Statistic,
  message,
  Popconfirm,
  DatePicker,
  InputNumber,
  Tag,
  Tooltip,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, DollarOutlined } from "@ant-design/icons";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { vatTransactionApi, vatClientApi } from "../../api/vatApi";
import { IVatTransaction, VatTransactionType, VatTransactionStatus } from "../../types/vatTransaction.type";
import { IVatClient } from "../../types/vatClient.type";
import { formatNumber } from "../../utils/formatNumber";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import dayjs from "dayjs";

const { Option } = Select;
const { TextArea } = Input;

const VatTransactionsPage: React.FC = () => {
  const { exchangeRate } = useExchangeRate();
  const [transactions, setTransactions] = useState<IVatTransaction[]>([]);
  const [clients, setClients] = useState<IVatClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [calculatedAmounts, setCalculatedAmounts] = useState({
    vatAmount: { USD: 0, UZS: 0 },
    totalAmount: { USD: 0, UZS: 0 }
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<IVatTransaction | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<IVatTransaction | null>(null);
  const [form] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [stats, setStats] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState<any>({});
  const { loading: downloadLoading, downloadFile } = useDownloadFile();

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params: any = { ...filters };
      if (searchText) {
        params.search = searchText;
      }
      const response = await vatTransactionApi.getAll(params);
      setTransactions(response.data);
    } catch (error) {
      message.error("Ошибка при загрузке транзакций НДС");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    setClientsLoading(true);
    try {
      const response = await vatClientApi.getAll();
      setClients(response.data);
    } catch (error) {
      message.error("Ошибка при загрузке клиентов");
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await vatTransactionApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error("Ошибка при загрузке статистики");
    } finally {
      setStatsLoading(false);
    }
  };

  const handleExport = () => {
    const exportParams: { [key: string]: any } = {};
    
    // Add filters if they exist
    if (filters.transactionType) {
      exportParams.transactionType = filters.transactionType;
    }
    if (filters.status) {
      exportParams.status = filters.status;
    }
    if (filters.clientId) {
      exportParams.clientId = filters.clientId;
    }

    downloadFile(`/export/vat-transactions`, "vat_transactions_data.xlsx", exportParams);
  };

  useEffect(() => {
    fetchTransactions();
    fetchClients();
    fetchStats();
  }, [searchText, filters]);

  const handleCreate = () => {
    if (!exchangeRate) {
      message.warning("Сначала установите курс обмена в настройках");
      return;
    }

    setEditingTransaction(null);
    form.resetFields();
    form.setFieldsValue({
      exchangeRate: exchangeRate,
      vatRate: 12,
    });
    setCalculatedAmounts({
      vatAmount: { USD: 0, UZS: 0 },
      totalAmount: { USD: 0, UZS: 0 }
    });
    setModalVisible(true);
  };

  const handleEdit = (transaction: IVatTransaction) => {
    setEditingTransaction(transaction);
    form.setFieldsValue({
      clientId: transaction.clientId,
      transactionType: transaction.transactionType,
      description: transaction.description,
      amountWithoutVat: transaction.amountWithoutVat,
      vatAmount: transaction.vatAmount,
      totalAmount: transaction.totalAmount,
      exchangeRate: transaction.exchangeRate,
      vatRate: transaction.vatRate,
      notes: transaction.notes,
    });
    setCalculatedAmounts({
      vatAmount: transaction.vatAmount,
      totalAmount: transaction.totalAmount
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    setDeleteLoading(id);
    try {
      await vatTransactionApi.delete(id);
      message.success("Транзакция НДС успешно удалена");
      fetchTransactions();
      fetchStats();
    } catch (error) {
      message.error("Ошибка при удалении транзакции НДС");
    } finally {
      setDeleteLoading(null);
    }
  };

  const handlePayment = (transaction: IVatTransaction) => {
    if (!exchangeRate) {
      message.warning("Сначала установите курс обмена в настройках");
      return;
    }

    setSelectedTransaction(transaction);
    paymentForm.setFieldsValue({
      paidAmount: transaction.totalAmount,
      paymentDate: dayjs(),
    });
    setPaymentModalVisible(true);
  };

  // Функции для автоматического расчета оплаты
  const handlePaidUSDChange = (value: any) => {
    if (value === null || !exchangeRate) return;

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    const uzsValue = calculateUZSFromUSD(numValue);

    paymentForm.setFieldsValue({
      paidAmount: {
        USD: numValue,
        UZS: uzsValue
      }
    });
  };

  const handlePaidUZSChange = (value: any) => {
    if (value === null || !exchangeRate) return;

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    const usdValue = calculateUSDFromUZS(numValue);

    paymentForm.setFieldsValue({
      paidAmount: {
        USD: usdValue,
        UZS: numValue
      }
    });
  };

  const handlePaymentSubmit = async (values: any) => {
    setPaymentLoading(true);
    try {
      await vatTransactionApi.markAsPaid(selectedTransaction!._id, {
        paidAmount: values.paidAmount,
        paymentDate: values.paymentDate.format("DD.MM.YYYY HH:mm"),
      });
      message.success("Транзакция отмечена как оплаченная");
      setPaymentModalVisible(false);
      fetchTransactions();
      fetchStats();
    } catch (error) {
      message.error("Ошибка при обновлении статуса оплаты");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitLoading(true);
    try {
      if (editingTransaction) {
        await vatTransactionApi.update(editingTransaction._id, values);
        message.success("Транзакция НДС успешно обновлена");
      } else {
        await vatTransactionApi.create(values);
        message.success("Транзакция НДС успешно создана");
      }
      setModalVisible(false);
      fetchTransactions();
      fetchStats();
    } catch (error) {
      message.error("Ошибка при сохранении транзакции НДС");
    } finally {
      setSubmitLoading(false);
    }
  };

  const getStatusColor = (status: VatTransactionStatus) => {
    switch (status) {
      case VatTransactionStatus.PAID:
        return "green";
      case VatTransactionStatus.PARTIALLY_PAID:
        return "blue";
      case VatTransactionStatus.PENDING:
        return "orange";
      case VatTransactionStatus.CANCELLED:
        return "red";
      default:
        return "default";
    }
  };

  // Функции для автоматического расчета валют
  const calculateUZSFromUSD = (usdAmount: number) => {
    if (!exchangeRate) return 0;
    return Math.round(usdAmount * exchangeRate);
  };

  const calculateUSDFromUZS = (uzsAmount: number) => {
    if (!exchangeRate) return 0;
    return Math.round((uzsAmount / exchangeRate) * 100) / 100; // Округляем до 2 знаков после запятой
  };

    const handleUSDChange = (value: any, field: 'USD' | 'UZS') => {
    if (value === null || !exchangeRate) return;
    
    const vatRate = form.getFieldValue('vatRate') || 12;

    if (field === 'USD') {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      const uzsValue = calculateUZSFromUSD(numValue);

      // Рассчитываем НДС и общую сумму
      const vatAmountUSD = Math.round((numValue * vatRate / 100) * 100) / 100;
      const vatAmountUZS = Math.round(uzsValue * vatRate / 100);
      const totalAmountUSD = Math.round((numValue + vatAmountUSD) * 100) / 100;
      const totalAmountUZS = uzsValue + vatAmountUZS;

      const newCalculatedAmounts = {
        vatAmount: { USD: vatAmountUSD, UZS: vatAmountUZS },
        totalAmount: { USD: totalAmountUSD, UZS: totalAmountUZS }
      };

      form.setFieldsValue({
        amountWithoutVat: {
          USD: numValue,
          UZS: uzsValue
        },
        vatAmount: {
          USD: vatAmountUSD,
          UZS: vatAmountUZS
        },
        totalAmount: {
          USD: totalAmountUSD,
          UZS: totalAmountUZS
        }
      });

      setCalculatedAmounts(newCalculatedAmounts);
    }
  };

  const handleUZSChange = (value: any, field: 'USD' | 'UZS') => {
    if (value === null || !exchangeRate) return;

    const vatRate = form.getFieldValue('vatRate') || 12;

    if (field === 'UZS') {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      const usdValue = calculateUSDFromUZS(numValue);

      // Рассчитываем НДС и общую сумму
      const vatAmountUZS = Math.round(numValue * vatRate / 100);
      const vatAmountUSD = Math.round((usdValue * vatRate / 100) * 100) / 100;
      const totalAmountUZS = numValue + vatAmountUZS;
      const totalAmountUSD = Math.round((usdValue + vatAmountUSD) * 100) / 100;

      const newCalculatedAmounts = {
        vatAmount: { USD: vatAmountUSD, UZS: vatAmountUZS },
        totalAmount: { USD: totalAmountUSD, UZS: totalAmountUZS }
      };

      form.setFieldsValue({
        amountWithoutVat: {
          USD: usdValue,
          UZS: numValue
        },
        vatAmount: {
          USD: vatAmountUSD,
          UZS: vatAmountUZS
        },
        totalAmount: {
          USD: totalAmountUSD,
          UZS: totalAmountUZS
        }
      });

      setCalculatedAmounts(newCalculatedAmounts);
    }
  };

  const handleVatRateChange = (value: any) => {
    if (value === null) return;

    const amountWithoutVat = form.getFieldValue('amountWithoutVat');
    if (!amountWithoutVat) return;

    const vatRate = typeof value === 'string' ? parseFloat(value) : value;

    // Пересчитываем НДС и общую сумму при изменении ставки НДС
    const vatAmountUSD = Math.round((amountWithoutVat.USD * vatRate / 100) * 100) / 100;
    const vatAmountUZS = Math.round(amountWithoutVat.UZS * vatRate / 100);
    const totalAmountUSD = Math.round((amountWithoutVat.USD + vatAmountUSD) * 100) / 100;
    const totalAmountUZS = amountWithoutVat.UZS + vatAmountUZS;

    const newCalculatedAmounts = {
      vatAmount: { USD: vatAmountUSD, UZS: vatAmountUZS },
      totalAmount: { USD: totalAmountUSD, UZS: totalAmountUZS }
    };

    form.setFieldsValue({
      vatAmount: {
        USD: vatAmountUSD,
        UZS: vatAmountUZS
      },
      totalAmount: {
        USD: totalAmountUSD,
        UZS: totalAmountUZS
      }
    });

    setCalculatedAmounts(newCalculatedAmounts);
  };

  // Функция для расчета от общей суммы
  const handleTotalAmountChange = (value: any, field: 'USD' | 'UZS') => {
    if (value === null || !exchangeRate) return;

    const vatRate = form.getFieldValue('vatRate') || 12;

    if (field === 'USD') {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      const uzsValue = calculateUZSFromUSD(numValue);

      // Рассчитываем сумму без НДС от общей суммы
      const amountWithoutVatUSD = Math.round((numValue / (1 + vatRate / 100)) * 100) / 100;
      const amountWithoutVatUZS = Math.round(uzsValue / (1 + vatRate / 100));
      const vatAmountUSD = Math.round((amountWithoutVatUSD * vatRate / 100) * 100) / 100;
      const vatAmountUZS = Math.round(amountWithoutVatUZS * vatRate / 100);

      const newCalculatedAmounts = {
        vatAmount: { USD: vatAmountUSD, UZS: vatAmountUZS },
        totalAmount: { USD: numValue, UZS: uzsValue }
      };

      form.setFieldsValue({
        amountWithoutVat: {
          USD: amountWithoutVatUSD,
          UZS: amountWithoutVatUZS
        },
        vatAmount: {
          USD: vatAmountUSD,
          UZS: vatAmountUZS
        },
        totalAmount: {
          USD: numValue,
          UZS: uzsValue
        }
      });

      setCalculatedAmounts(newCalculatedAmounts);
    } else if (field === 'UZS') {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      const usdValue = calculateUSDFromUZS(numValue);

      // Рассчитываем сумму без НДС от общей суммы
      const amountWithoutVatUZS = Math.round(numValue / (1 + vatRate / 100));
      const amountWithoutVatUSD = Math.round((usdValue / (1 + vatRate / 100)) * 100) / 100;
      const vatAmountUZS = Math.round(amountWithoutVatUZS * vatRate / 100);
      const vatAmountUSD = Math.round((amountWithoutVatUSD * vatRate / 100) * 100) / 100;

      const newCalculatedAmounts = {
        vatAmount: { USD: vatAmountUSD, UZS: vatAmountUZS },
        totalAmount: { USD: usdValue, UZS: numValue }
      };

      form.setFieldsValue({
        amountWithoutVat: {
          USD: amountWithoutVatUSD,
          UZS: amountWithoutVatUZS
        },
        vatAmount: {
          USD: vatAmountUSD,
          UZS: vatAmountUZS
        },
        totalAmount: {
          USD: usdValue,
          UZS: numValue
        }
      });

      setCalculatedAmounts(newCalculatedAmounts);
    }
  };

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
      sorter: (a: IVatTransaction, b: IVatTransaction) =>
        dayjs(a.date, "DD.MM.YYYY HH:mm").unix() - dayjs(b.date, "DD.MM.YYYY HH:mm").unix(),
    },
    {
      title: "Номер счета",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
    },
    {
      title: "Клиент",
      dataIndex: "clientName",
      key: "clientName",
      filters: clients.map(client => ({ text: client.name, value: client.name })),
      onFilter: (value: any, record: IVatTransaction) => record.clientName === value,
    },
    {
      title: "Тип",
      dataIndex: "transactionType",
      key: "transactionType",
      render: (value: VatTransactionType) => (
        <Tag color={value === VatTransactionType.SALE ? "blue" : "green"}>
          {value}
        </Tag>
      ),
      filters: Object.values(VatTransactionType).map(type => ({ text: type, value: type })),
      onFilter: (value: any, record: IVatTransaction) => record.transactionType === value,
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: (
        <Tooltip title="Сумма транзакции без учета налога на добавленную стоимость (НДС)">
          Сумма без НДС
        </Tooltip>
      ),
      dataIndex: "amountWithoutVat",
      key: "amountWithoutVat",
      render: (value: { USD: number; UZS: number }) => (
        <div>
          <div>{formatNumber(value.UZS)} UZS</div>
          <small>{formatNumber(value.USD)} USD</small>
        </div>
      ),
      sorter: (a: IVatTransaction, b: IVatTransaction) => a.amountWithoutVat.UZS - b.amountWithoutVat.UZS,
    },
    {
      title: (
        <Tooltip title="Сумма налога на добавленную стоимость (НДС) по ставке. При продаже - наш долг, при покупке - наш долг">
          НДС
        </Tooltip>
      ),
      dataIndex: "vatAmount",
      key: "vatAmount",
      render: (value: { USD: number; UZS: number }) => (
        <div>
          <div>{formatNumber(value.UZS)} UZS</div>
          <small>{formatNumber(value.USD)} USD</small>
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Общая сумма транзакции включая НДС (Сумма без НДС + НДС)">
          Общая сумма с НДС
        </Tooltip>
      ),
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (value: { USD: number; UZS: number }) => (
        <div>
          <div>{formatNumber(value.UZS)} UZS</div>
          <small>{formatNumber(value.USD)} USD</small>
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Остаток к оплате (Общая сумма - Оплаченная сумма)">
          Остаток
        </Tooltip>
      ),
      key: "remainingAmount",
      render: (_: any, record: IVatTransaction) => {
        const totalUZS = record.totalAmount.UZS;
        const paidUZS = record.paidAmount?.UZS || 0;
        const remainingUZS = totalUZS - paidUZS;
        const remainingUSD = record.totalAmount.USD - (record.paidAmount?.USD || 0);

        return (
          <div>
            <div style={{ color: remainingUZS > 0 ? 'red' : 'green' }}>
              {formatNumber(Math.round(remainingUZS))} UZS
            </div>
            <small style={{ color: remainingUZS > 0 ? 'red' : 'green' }}>
              {formatNumber(Math.round(remainingUSD * 100) / 100)} USD
            </small>
          </div>
        );
      },
      sorter: (a: IVatTransaction, b: IVatTransaction) => {
        const aRemaining = a.totalAmount.UZS - (a.paidAmount?.UZS || 0);
        const bRemaining = b.totalAmount.UZS - (b.paidAmount?.UZS || 0);
        return aRemaining - bRemaining;
      },
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      render: (value: VatTransactionStatus) => (
        <Tag color={getStatusColor(value)}>
          {value}
        </Tag>
      ),
      filters: Object.values(VatTransactionStatus).map(status => ({ text: status, value: status })),
      onFilter: (value: any, record: IVatTransaction) => record.status === value,
    },
    {
      title: "Действия",
      key: "actions",
      render: (_: any, record: IVatTransaction) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Редактировать
          </Button>
          {(record.status === VatTransactionStatus.PENDING || record.status === VatTransactionStatus.PARTIALLY_PAID) && (
            <Button
              type="link"
              icon={<DollarOutlined />}
              onClick={() => handlePayment(record)}
            >
              Оплатить
            </Button>
          )}
          <Popconfirm
            title="Вы уверены, что хотите удалить эту транзакцию?"
            onConfirm={() => handleDelete(record._id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={deleteLoading === record._id}
            >
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card title="Транзакции НДС" extra={
        <Space>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Курс обмена: {exchangeRate ? `${exchangeRate} UZS/USD` : 'Не установлен'}
          </div>
          <Input
            placeholder="Поиск по описанию, клиенту или номеру счета"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
          <Button
            type="default"
            loading={downloadLoading}
            icon={<PiMicrosoftExcelLogoFill />}
            onClick={handleExport}
          >
            Экспорт в Excel
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Добавить транзакцию
          </Button>
        </Space>
      }>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          {statsLoading ? (
            <Col span={24}>
              <Card>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  Загрузка статистики...
                </div>
              </Card>
            </Col>
          ) : (
            stats.map((stat) => (
              <Col span={6} key={stat._id}>
                <Card>
                  <Statistic
                    title={stat._id}
                    value={stat.count}
                    suffix="транзакций"
                  />
                  <div style={{ marginTop: 8 }}>
                    <small>Сумма: {formatNumber(stat.totalAmount)}</small>
                    <br />
                    <small>Оплачено: {formatNumber(stat.paidAmount)}</small>
                  </div>
                </Card>
              </Col>
            ))
          )}
        </Row>

        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="_id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} из ${total} транзакций`,
          }}
          scroll={{ x: "max-content" }}
        />
      </Card>

      {/* Modal для создания/редактирования транзакции */}
      <Modal
        title={editingTransaction ? "Редактировать транзакцию НДС" : "Добавить транзакцию НДС"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="clientId"
                label="Клиент"
                rules={[{ required: true, message: "Выберите клиента" }]}
              >
                <Select placeholder="Выберите клиента">
                  {clients.map(client => (
                    <Option key={client._id} value={client._id}>
                      {client.name} ({client.tin})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="transactionType"
                label="Тип транзакции"
                rules={[{ required: true, message: "Выберите тип транзакции" }]}
              >
                <Select>
                  {Object.values(VatTransactionType).map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: "Введите описание" }]}
          >
            <TextArea rows={3} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name={["amountWithoutVat", "USD"]}
                label="Сумма без НДС (USD)"
                rules={[{ required: true, message: "Введите сумму" }]}
                extra="При вводе автоматически рассчитывается UZS, НДС и общая сумма"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  onChange={(value) => handleUSDChange(value, 'USD')}
                  placeholder="Введите сумму в USD"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name={["amountWithoutVat", "UZS"]}
                label="Сумма без НДС (UZS)"
                rules={[{ required: true, message: "Введите сумму" }]}
                extra="При вводе автоматически рассчитывается USD, НДС и общая сумма"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  onChange={(value) => handleUZSChange(value, 'UZS')}
                  placeholder="Введите сумму в UZS"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name={["totalAmount", "USD"]}
                label="Общая сумма с НДС (USD)"
                extra="При вводе автоматически рассчитывается сумма без НДС и НДС"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  onChange={(value) => handleTotalAmountChange(value, 'USD')}
                  placeholder="Введите общую сумму в USD"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name={["totalAmount", "UZS"]}
                label="Общая сумма с НДС (UZS)"
                extra="При вводе автоматически рассчитывается сумма без НДС и НДС"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  onChange={(value) => handleTotalAmountChange(value, 'UZS')}
                  placeholder="Введите общую сумму в UZS"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="exchangeRate"
                label="Курс обмена"
                rules={[{ required: true, message: "Введите курс обмена" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  disabled
                  placeholder={`Текущий курс: ${exchangeRate || 'Не установлен'}`}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="vatRate"
                label="Ставка НДС (%)"
                initialValue={12}
                rules={[{ required: true, message: "Введите ставку НДС" }]}
              >
                <InputNumber
                  min={0}
                  max={100}
                  style={{ width: "100%" }}
                  onChange={handleVatRateChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name={["vatAmount", "USD"]}
                label="НДС (USD)"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  disabled
                  placeholder="Рассчитывается автоматически"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={["vatAmount", "UZS"]}
                label="НДС (UZS)"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  disabled
                  placeholder="Рассчитывается автоматически"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Итоговая сумма"
                style={{ textAlign: 'center', paddingTop: '32px' }}
              >
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                  <div>НДС: {formatNumber(calculatedAmounts.vatAmount.USD)} USD</div>
                  <div>Общая: {formatNumber(calculatedAmounts.totalAmount.USD)} USD</div>
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  <div>НДС: {formatNumber(calculatedAmounts.vatAmount.UZS)} UZS</div>
                  <div>Общая: {formatNumber(calculatedAmounts.totalAmount.UZS)} UZS</div>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Примечания"
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {editingTransaction ? "Обновить" : "Создать"}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal для оплаты */}
      <Modal
        title="Отметить как оплаченную"
        open={paymentModalVisible}
        onCancel={() => setPaymentModalVisible(false)}
        footer={null}
      >
        <div style={{ marginBottom: 16, fontSize: '12px', color: '#666' }}>
          Курс обмена: {exchangeRate ? `${exchangeRate} UZS/USD` : 'Не установлен'}
        </div>
        <Form
          form={paymentForm}
          layout="vertical"
          onFinish={handlePaymentSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name={["paidAmount", "USD"]}
                label="Оплаченная сумма (USD)"
                rules={[{ required: true, message: "Введите сумму" }]}
                extra="При вводе USD автоматически рассчитывается UZS"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  onChange={handlePaidUSDChange}
                  placeholder="Введите сумму в USD"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={["paidAmount", "UZS"]}
                label="Оплаченная сумма (UZS)"
                rules={[{ required: true, message: "Введите сумму" }]}
                extra="При вводе UZS автоматически рассчитывается USD"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  onChange={handlePaidUZSChange}
                  placeholder="Введите сумму в UZS"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="paymentDate"
            label="Дата оплаты"
            rules={[{ required: true, message: "Выберите дату оплаты" }]}
          >
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={paymentLoading}>
                Подтвердить оплату
              </Button>
              <Button onClick={() => setPaymentModalVisible(false)}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VatTransactionsPage; 